import { BaseService } from '@/infrastructure/api/base.service'
import axios from 'axios'
import type { AiSearchResponse } from '@/types/ai-search.types'
import { AI_CONFIG } from '@/config/ai.config'

// Module-level token cache (shared across all AiSearchService instances)
let _aiToken: string | null = null
let _aiTokenExpiryMs = 0
let _aiTokenInFlight: Promise<string> | null = null

function loadFromStorage(): void {
  try {
    const t = localStorage.getItem(AI_CONFIG.tokenCacheKey)
    const e = Number(localStorage.getItem(AI_CONFIG.tokenExpiryCacheKey) || 0)
    if (t && e && Date.now() < e) {
      _aiToken = t
      _aiTokenExpiryMs = e
    }
  } catch {
    /* localStorage unavailable (private browsing) — rely on module vars */
  }
}

function saveToStorage(): void {
  try {
    localStorage.setItem(AI_CONFIG.tokenCacheKey, _aiToken || '')
    localStorage.setItem(AI_CONFIG.tokenExpiryCacheKey, String(_aiTokenExpiryMs || 0))
  } catch {
    /* localStorage unavailable — token cached in memory only */
  }
}

function isValid(): boolean {
  return !!_aiToken && Date.now() < _aiTokenExpiryMs
}

function decodeJwtExpMs(token: string): number | null {
  try {
    const b64 = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/')
    if (!b64) return null
    const payload = JSON.parse(atob(b64))
    return payload?.exp ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

// Exported for testing — allows resetting module-level state between tests
export function _resetTokenCache(): void {
  _aiToken = null
  _aiTokenExpiryMs = 0
  _aiTokenInFlight = null
  try {
    localStorage.removeItem(AI_CONFIG.tokenCacheKey)
    localStorage.removeItem(AI_CONFIG.tokenExpiryCacheKey)
  } catch {
    /* ignore */
  }
}

export class AiSearchService extends BaseService {
  protected readonly aiAxios = axios.create({
    baseURL: AI_CONFIG.apiUrl,
    timeout: AI_CONFIG.apiTimeoutMs,
    headers: { 'Content-Type': 'application/json' },
  })

  constructor() {
    super('')
    if (!_aiToken) loadFromStorage()
  }

  async fetchToken(): Promise<string> {
    if (isValid()) return _aiToken!
    if (_aiTokenInFlight) return _aiTokenInFlight
    _aiTokenInFlight = this._fetchFreshToken().finally(() => {
      _aiTokenInFlight = null
    })
    return _aiTokenInFlight
  }

  private async _fetchFreshToken(): Promise<string> {
    const raw = await this.post<
      string | { token?: string; access_token?: string; expires_in?: number }
    >('/V2/LookAlike/FetchToken')

    const token =
      typeof raw === 'string' ? raw : raw?.token || raw?.access_token || ''
    if (!token) throw new Error('No token returned from FetchToken')

    const jwtExp = decodeJwtExpMs(token)
    const ttlFromApi =
      typeof raw === 'object' && raw?.expires_in ? raw.expires_in * 1000 : 0
    let expiry =
      jwtExp ||
      (ttlFromApi ? Date.now() + ttlFromApi : Date.now() + AI_CONFIG.defaultTokenTtlMs)
    expiry -= AI_CONFIG.tokenEarlyRefreshMs

    _aiToken = token
    _aiTokenExpiryMs = expiry
    saveToStorage()
    return token
  }

  async queryAiSearch(
    query: string,
    type: string,
    username: string,
  ): Promise<AiSearchResponse> {
    const token = await this.fetchToken()
    // Uses dedicated axios instance (not app httpClient) because
    // the external AI API uses its own auth, not the app's JWT
    const response = await this.aiAxios.post(
      AI_CONFIG.searchEndpoint,
      { username, query, type },
      { headers: { Authorization: `Bearer ${token}` } },
    )
    // External API wraps payload in { data: {...} } envelope
    return response.data.data
  }

  async getCompanySuggestion(input: string): Promise<string | null> {
    try {
      const data = await this.get<{
        Companies?: Array<{ Value?: string }>
      }>(`/V2/SearchBase/GetSuggestions?input=${encodeURIComponent(input.trim())}`)
      const list = Array.isArray(data?.Companies) ? data.Companies : []
      return list.length > 0 ? (list[0]?.Value ?? null) : null
    } catch {
      return null
    }
  }
}
